import type { ErrorInfo, ReactNode } from 'react'
import React, { Component, createContext, useContext } from 'react'
import { AlertCircle } from 'lucide-react'
import './error-boundary.css'

/**
 * Internal context for passing reportError function to ErrorBoundaryWithReporter.
 * This avoids the circular dependency with ErrorReporterContext.
 */
type ReportErrorFn = (error: Error, componentStack?: string) => void
const ErrorReportFnContext = createContext<ReportErrorFn | null>(null)

/**
 * Provider to inject the reportError function into the ErrorBoundary tree.
 * Used by ErrorReporterProvider to connect error boundaries to error reporting.
 */
export function ErrorReportFnProvider({
	reportError,
	children,
}: {
	reportError: ReportErrorFn
	children: ReactNode
}) {
	return (
		<ErrorReportFnContext.Provider value={reportError}>
			{children}
		</ErrorReportFnContext.Provider>
	)
}

/**
 * Hook to get the reportError function if available.
 * Returns null if not within an ErrorReportFnProvider.
 */
function useReportErrorFn(): ReportErrorFn | null {
	return useContext(ErrorReportFnContext)
}

interface ErrorBoundaryProps {
	children: ReactNode
	fallback?: ReactNode
	onError?: (error: Error, errorInfo: ErrorInfo) => void
	/** Function to open the error report dialog (injected by ErrorBoundaryWithReporter) */
	onReportError?: (error: Error, componentStack: string) => void
}

interface ErrorBoundaryState {
	hasError: boolean
	error: Error | null
	componentStack: string | null
}

/**
 * Error Boundary Component
 *
 * Catches React rendering errors in child components and displays a fallback UI
 * instead of crashing the entire application.
 *
 * When used with ErrorBoundaryWithReporter, automatically opens the ErrorReportDialog
 * when an error occurs, allowing users to report the issue immediately.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props)
		this.state = {
			hasError: false,
			error: null,
			componentStack: null,
		}
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return {
			hasError: true,
			error,
		}
	}

	override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log error to console in development
		console.error('ErrorBoundary caught an error:', error, errorInfo)

		// Store component stack for reporting
		this.setState({ componentStack: errorInfo.componentStack ?? null })

		// Call optional error handler (could be used for error reporting service)
		this.props.onError?.(error, errorInfo)

		// Automatically report error to ErrorReportDialog if reporter is available
		if (this.props.onReportError) {
			this.props.onReportError(error, errorInfo.componentStack ?? '')
		}
	}

	override render(): ReactNode {
		if (this.state.hasError) {
			// Render custom fallback UI if provided
			if (this.props.fallback) {
				return this.props.fallback
			}

			// If error reporter is available, render nothing - the ErrorReportDialog handles it
			if (this.props.onReportError) {
				return null
			}

			// Default fallback UI - only shown when no error reporter is connected
			return (
				<div className="error-boundary-fallback">
					<AlertCircle className="error-boundary-icon" />
					<h2 className="error-boundary-title">Something went wrong</h2>
					<p className="error-boundary-message">
						{this.props.onReportError
							? 'An error report dialog has opened. You can reload the page to try again.'
							: 'An error occurred while rendering this component. Please try refreshing the page.'
						}
					</p>

					<div className="error-boundary-actions">
						<button
							type="button"
							onClick={() => window.location.reload()}
							className="error-boundary-button"
							data-variant="primary"
						>
							Reload page
						</button>
					</div>

					{/* SECURITY: Only show stack traces in development to prevent information disclosure */}
					{this.state.error && import.meta.env.DEV && (
						<details className="error-boundary-details">
							<summary className="error-boundary-summary">Technical details (dev only)</summary>
							<pre className="error-boundary-stack">
								{this.state.error.toString()}
								{'\n'}
								{this.state.error.stack}
							</pre>
						</details>
					)}
				</div>
			)
		}

		return this.props.children
	}
}

/**
 * Higher-order component to wrap a component with an error boundary
 *
 * Usage:
 * ```tsx
 * export const MyComponent = withErrorBoundary(() => {
 *   // Component code
 * })
 * ```
 */
export function withErrorBoundary<P extends object>(
	Component: React.ComponentType<P>,
	fallback?: ReactNode,
): React.ComponentType<P> {
	return (props: P) => (
		<ErrorBoundary fallback={fallback}>
			<Component {...props} />
		</ErrorBoundary>
	)
}

/**
 * ErrorBoundary wrapper that connects to the ErrorReporter context.
 *
 * This is a functional component that uses the ErrorReportFnContext
 * and passes the reportError function to the class-based ErrorBoundary.
 *
 * When an error is caught, it automatically opens the ErrorReportDialog,
 * eliminating the need for users to click a "Report this error" button.
 *
 * Usage:
 * ```tsx
 * // Wrap your app with ErrorReportFnProvider (done by ErrorReporterProvider)
 * <ErrorReportFnProvider reportError={reportError}>
 *   <ErrorBoundaryWithReporter>
 *     <MyComponent />
 *   </ErrorBoundaryWithReporter>
 * </ErrorReportFnProvider>
 * ```
 */
interface ErrorBoundaryWithReporterProps {
	children: ReactNode
	fallback?: ReactNode
}

export function ErrorBoundaryWithReporter({ children, fallback }: ErrorBoundaryWithReporterProps) {
	// Get reportError from context (injected by ErrorReporterProvider via ErrorReportFnProvider)
	const reportError = useReportErrorFn()

	return (
		<ErrorBoundary fallback={fallback} onReportError={reportError ?? undefined}>
			{children}
		</ErrorBoundary>
	)
}
